import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Campaign as CampaignIcon,
  Mic as MicIcon,
  Favorite as FavoriteIcon,
  Topic as TopicIcon,
  AutoFixHigh as AutoFixHighIcon,
  Shield as ShieldIcon,
  Visibility as VisibilityIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Info as InfoIcon,
  SupportAgent as SupportAgentIcon,
} from '@mui/icons-material';
import { AppColors } from '../theme/colors';
import { useIsDesktop } from '../hooks/useMediaQuery';
import { useAuth } from '../hooks/useAuth';
import { useCompany } from '../hooks/useAuth';
import { getBoxTemplate } from '../constants/industryTemplates';
import { ResponsiveUserLayout } from '../components/ResponsiveUserLayout';
import { ResponsiveAdminLayout } from '../components/ResponsiveAdminLayout';

export const AboutAppPage: React.FC = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { user, loading } = useAuth();
  const { company } = useCompany(user?.uid || null);
  const isAdmin = user?.userType === 'admin' || user?.userType === 'superadmin';

  // ローディング状態の処理
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>読み込み中...</Typography>
      </Box>
    );
  }

  const renderContent = () => {
    return (
      <Box sx={{ 
        px: 2,
        pb: 2,
        pt: isAdmin && isDesktop ? 0 : 0,
        // 設定画面のプライバシー見出しと同じ上余白に合わせる（ユーザー表示時は常に mt:2）
        mt: isAdmin ? (isDesktop ? 0 : 2) : 2,
      }}>
          {/* アプリ概要 */}
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 'bold',
              color: AppColors.primary,
              mb: 2,
            }}
          >
            アプリ概要
          </Typography>
          <Card
            sx={{
              boxShadow: 2,
              borderRadius: 3,
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box
                  component="img"
                  src="/app_icon.png"
                  alt="LiaVoice"
                  sx={{
                    width: 28,
                    height: 28,
                    mr: 2,
                    mt: 0.5,
                    borderRadius: 1,
                  }}
                />
                <Box>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 'bold',
                      mb: 0.5,
                    }}
                  >
                    LiaVoice（リアボイス）
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: AppColors.textSecondary,
                      mb: 1,
                    }}
                  >
                    職場の風土改善のための完全匿名意見投稿プラットフォーム
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      lineHeight: 1.5,
                      color: AppColors.textPrimary,
                    }}
                  >
                    LiaVoice（リアボイス）は、従業員が完全匿名で意見や感謝を共有できるアプリです。
                    心理的安全性を確保しながら、組織の改善に貢献できる環境を提供します。
                    あなたの声が、より良い職場づくりの第一歩となります。
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* 主な機能 */}
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 'bold',
              color: AppColors.primary,
              mb: 2,
            }}
          >
            主な機能
          </Typography>
          
          <Card sx={{ boxShadow: 2, borderRadius: 3, mb: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <MicIcon sx={{ color: AppColors.primary, fontSize: 28, mr: 2, mt: 0.5 }} />
                <Box>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 'bold',
                      mb: 1,
                    }}
                  >
                    {getBoxTemplate(company?.industryType, 'voice').name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      lineHeight: 1.5,
                      color: AppColors.textPrimary,
                    }}
                  >
                    職場への意見や提案を匿名で投稿できます。
                    日頃感じている改善点や、言いにくかったことを安心して共有できます。
                    ※投稿したメッセージは管理者のみが閲覧可能です。
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ boxShadow: 2, borderRadius: 3, mb: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <FavoriteIcon sx={{ color: AppColors.primary, fontSize: 28, mr: 2, mt: 0.5 }} />
                <Box>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 'bold',
                      mb: 1,
                    }}
                  >
                    {getBoxTemplate(company?.industryType, 'thanks').name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      lineHeight: 1.5,
                      color: AppColors.textPrimary,
                    }}
                  >
                    同僚や上司への感謝の気持ちを伝えられます。
                    管理者が承認すると、みんなで共有してメッセージを見ることができます。
                    感謝を共有することで、職場の雰囲気を明るくします。
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ boxShadow: 2, borderRadius: 3, mb: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <TopicIcon sx={{ color: AppColors.primary, fontSize: 28, mr: 2, mt: 0.5 }} />
                <Box>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 'bold',
                      mb: 1,
                    }}
                  >
                    {getBoxTemplate(company?.industryType, 'theme').name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      lineHeight: 1.5,
                      color: AppColors.textPrimary,
                    }}
                  >
                    管理者が設定したテーマに対して意見を投稿できます。
                    特定のトピックについて、みんなの考えを集められます。
                    ※投稿したメッセージは管理者のみが閲覧可能です。
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ boxShadow: 2, borderRadius: 3, mb: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <AutoFixHighIcon sx={{ color: AppColors.primary, fontSize: 28, mr: 2, mt: 0.5 }} />
                <Box>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 'bold',
                      mb: 1,
                    }}
                  >
                    AI校閲機能
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      lineHeight: 1.5,
                      color: AppColors.textPrimary,
                    }}
                  >
                    投稿前にAIが文章をチェック。建設的な表現への修正提案や、
                    個人情報の自動検出で、より安全で建設的なコミュニケーションをサポートします。
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* 投稿の使い方 */}
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 'bold',
              color: AppColors.primary,
              mb: 2,
            }}
          >
            投稿の使い方
          </Typography>
          
          <Card sx={{ boxShadow: 2, borderRadius: 3, mb: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: AppColors.primary,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    flexShrink: 0,
                  }}
                >
                  <Typography sx={{ color: 'white', fontWeight: 'bold', fontSize: 16 }}>
                    1
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 'bold',
                      mb: 1,
                    }}
                  >
                    投稿タイプを選択
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      lineHeight: 1.5,
                      color: AppColors.textPrimary,
                    }}
                  >
                    ホーム画面の「新規投稿」ボタンを選択し、{getBoxTemplate(company?.industryType, 'voice').name}・{getBoxTemplate(company?.industryType, 'thanks').name}・{getBoxTemplate(company?.industryType, 'theme').name}のいずれかを選択します。
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ boxShadow: 2, borderRadius: 3, mb: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: AppColors.primary,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    flexShrink: 0,
                  }}
                >
                  <Typography sx={{ color: 'white', fontWeight: 'bold', fontSize: 16 }}>
                    2
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 'bold',
                      mb: 1,
                    }}
                  >
                    内容を入力
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      lineHeight: 1.5,
                      color: AppColors.textPrimary,
                    }}
                  >
                    あなたの想いや意見を自由に記入してください。
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ boxShadow: 2, borderRadius: 3, mb: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: AppColors.primary,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    flexShrink: 0,
                  }}
                >
                  <Typography sx={{ color: 'white', fontWeight: 'bold', fontSize: 16 }}>
                    3
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 'bold',
                      mb: 1,
                    }}
                  >
                    AIで校閲
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      lineHeight: 1.5,
                      color: AppColors.textPrimary,
                    }}
                  >
                    AI校閲機能で文章をチェック。建設的な表現への修正提案で、より安全なコミュニケーションをサポートします。
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ boxShadow: 2, borderRadius: 3, mb: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: AppColors.primary,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    flexShrink: 0,
                  }}
                >
                  <Typography sx={{ color: 'white', fontWeight: 'bold', fontSize: 16 }}>
                    4
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 'bold',
                      mb: 1,
                    }}
                  >
                    投稿する
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      lineHeight: 1.5,
                      color: AppColors.textPrimary,
                    }}
                  >
                    投稿ボタンを押すと、完全匿名で投稿されます。管理者も投稿者を特定できません。
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* 完全匿名システム */}
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 'bold',
              color: AppColors.primary,
              mb: 2,
            }}
          >
            完全匿名システム
          </Typography>
          <Card sx={{ boxShadow: 2, borderRadius: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <ShieldIcon sx={{ color: AppColors.primary, fontSize: 24, mr: 1.5, mt: 0.5 }} />
                <Box>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 'bold',
                      mb: 1,
                    }}
                  >
                    技術的に特定不可能
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      lineHeight: 1.5,
                      color: AppColors.textPrimary,
                    }}
                  >
                    LiaVoiceは、投稿者を技術的に特定できない設計になっています。
                    管理者を含め、誰もあなたの投稿を特定することはできません。
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* 管理者機能 */}
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 'bold',
              color: AppColors.primary,
              mb: 2,
            }}
          >
            管理者機能
          </Typography>
          
          <Card sx={{ boxShadow: 2, borderRadius: 3, mb: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <VisibilityIcon sx={{ color: AppColors.primary, fontSize: 24, mr: 1.5, mt: 0.5 }} />
                <Box>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 'bold',
                      mb: 1,
                    }}
                  >
                    投稿の確認
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      lineHeight: 1.5,
                      color: AppColors.textPrimary,
                    }}
                  >
                    匿名で投稿されたメッセージを確認することができます。
                    {getBoxTemplate(company?.industryType, 'voice').name}、{getBoxTemplate(company?.industryType, 'thanks').name}、{getBoxTemplate(company?.industryType, 'theme').name}の投稿をすべて閲覧できます。
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ boxShadow: 2, borderRadius: 3, mb: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <AdminPanelSettingsIcon sx={{ color: AppColors.primary, fontSize: 24, mr: 1.5, mt: 0.5 }} />
                <Box>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 'bold',
                      mb: 1,
                    }}
                  >
                    投稿の承認（{getBoxTemplate(company?.industryType, 'thanks').name}）
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      lineHeight: 1.5,
                      color: AppColors.textPrimary,
                    }}
                  >
                    {getBoxTemplate(company?.industryType, 'thanks').name}の投稿を承認・削除できます。
                    承認した投稿は、みんなで共有して見ることができるようになります。
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ boxShadow: 2, borderRadius: 3, mb: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <TopicIcon sx={{ color: AppColors.primary, fontSize: 24, mr: 1.5, mt: 0.5 }} />
                <Box>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 'bold',
                      mb: 1,
                    }}
                  >
                    テーマの作成
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      lineHeight: 1.5,
                      color: AppColors.textPrimary,
                    }}
                  >
                    管理者は従業員に意見を求めたいテーマを作成できます。
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

    </Box>
    );
  };

  if (isAdmin && isDesktop) {
    return (
      <ResponsiveAdminLayout pageTitle="アプリについて">
        <Box>
          {/* 基準の余白の状態: PCは最大幅1000px・中央寄せ */}
          <Box sx={{ 
            flex: 1,
            px: 2,
            pt: 2,
            pb: 0,
            maxWidth: '1000px',
            mx: 'auto',
            width: '100%',
          }}>
            {renderContent()}
          </Box>
        </Box>
      </ResponsiveAdminLayout>
    );
  }


  return (
    <ResponsiveUserLayout 
      maxWidth="1000px"
      topFullWidth={
        <>
          {/* モバイル用ヘッダー */}
          {!isDesktop && (
            <AppBar
              position="static"
              sx={{
                bgcolor: 'white',
                boxShadow: 'none',
                borderBottom: '1px solid #e0e0e0',
              }}
            >
              <Toolbar>
                <IconButton edge="start" color="inherit" onClick={() => navigate(-1)} sx={{ color: 'black' }}>
                  <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: 'black', fontWeight: 600 }}>
                  アプリについて
                </Typography>
              </Toolbar>
            </AppBar>
          )}
          {/* PC用ヘッダー */}
          {isDesktop && (
            <Box
              sx={{
                width: '100%',
                p: 2,
                bgcolor: 'white',
                borderBottom: '1px solid #e0e0e0',
              }}
            >
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: 'black',
                }}
              >
                アプリについて
              </Typography>
            </Box>
          )}
        </>
      }
    >
      {renderContent()}
    </ResponsiveUserLayout>
  );
};
